import {
  initialSupplierFormValues,
  NO_REQUIRED_SUPPLIER_FIELDS,
  SupplierFieldAccess,
  SupplierFormFieldKey,
  SupplierFormValues,
  SupplierRequiredFields,
  SupplierTransmissionType,
} from 'src/types/manage-suppliers/SupplierForm';
import { isValidEmail } from 'src/utils/strings';
import { getSupplierFormLengthErrors } from './supplierFieldLimits';

export { getSupplierFormLengthErrors, SUPPLIER_FIELD_MAX_LENGTHS } from './supplierFieldLimits';

export const CREATE_SUPPLIER_FIELD_ACCESS: SupplierFieldAccess = {
  supplierName: true,
  status: false,
  externalSupplierName: true,
  apId: true,
  accountNumber: true,
  address1: true,
  address2: true,
  city: true,
  stateProvince: true,
  zipPostalCode: true,
  country: true,
  contactName: true,
  phone: true,
  orderEmail: true,
  transmissionType: true,
};

export const fromCreateFormValues = (
  values: Omit<SupplierFormValues, 'status'>
): SupplierFormValues => ({
  ...initialSupplierFormValues,
  ...values,
});

export type SupplierFormChangeValue = string | SupplierTransmissionType;

export const isSupplierFormFieldKey = (field: string): field is SupplierFormFieldKey =>
  field in initialSupplierFormValues;

/** Display value for blank supplier fields in read-only UI. Blank fields stay empty. */
export const EMPTY_SUPPLIER_DISPLAY_VALUE = '';

/** Normalizes null/undefined/whitespace to `''`. Preserves `-` as a real user/API value. */
export const toSupplierFieldValue = (value: string | null | undefined): string =>
  (value ?? '').trim();

/** Removes tab, enter, and comma characters from phone input. Preserves spaces while typing. */
export const toSupplierPhoneValue = (value: string | null | undefined): string => {
  const normalized = value ?? '';

  if (!normalized.trim()) {
    return '';
  }

  return normalized.replace(/[\t\n\r,]/g, '');
};

export const normalizeSupplierName = (value: string): string => value.trim().toLowerCase();

export const validateOrderEmail = (
  orderEmail: string,
  transmissionType: SupplierTransmissionType,
  t: (key: string) => string
): string | undefined => {
  const trimmed = orderEmail.trim();

  if (transmissionType === 'email') {
    if (!trimmed) {
      return t('Order Email is required');
    }

    if (!isValidEmail(trimmed)) {
      return t('Please enter a valid email address');
    }

    return undefined;
  }

  if (trimmed && !isValidEmail(trimmed)) {
    return t('Please enter a valid email address');
  }

  return undefined;
};

export const getSupplierFormSyncErrors = ({
  values,
  fieldAccess,
  requiredFields = NO_REQUIRED_SUPPLIER_FIELDS,
  hasSupplierInheritance = false,
  t,
}: {
  values: SupplierFormValues;
  fieldAccess: Partial<Record<SupplierFormFieldKey, boolean>>;
  requiredFields?: SupplierRequiredFields;
  /** Supplier is Inherited / NotInherited from the management company. */
  hasSupplierInheritance?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}): Partial<Record<SupplierFormFieldKey, string>> => {
  const nextErrors: Partial<Record<SupplierFormFieldKey, string>> = {
    ...getSupplierFormLengthErrors(values, fieldAccess, t),
  };

  if (fieldAccess.supplierName !== false && !values.supplierName.trim()) {
    nextErrors.supplierName = t('Supplier Name is required');
  }

  if (fieldAccess.orderEmail !== false) {
    const orderEmailError = validateOrderEmail(values.orderEmail, values.transmissionType, t);
    if (orderEmailError) {
      nextErrors.orderEmail = orderEmailError;
    }
  } else if (values.orderEmail.trim()) {
    const orderEmailError = validateOrderEmail(values.orderEmail, values.transmissionType, t);
    if (orderEmailError) {
      nextErrors.orderEmail = orderEmailError;
    }
  }

  const hasApId = Boolean(values.apId.trim());
  const hasExternalSupplierName = Boolean(values.externalSupplierName.trim());

  if (fieldAccess.apId !== false && !hasApId) {
    if (hasExternalSupplierName) {
      nextErrors.apId = t('AP ID cannot be empty which has External Supplier Name.');
    } else if (requiredFields.apId && !hasSupplierInheritance) {
      nextErrors.apId = t('AP ID is required');
    }
  }

  if (
    fieldAccess.externalSupplierName !== false &&
    requiredFields.externalSupplierName &&
    !hasExternalSupplierName
  ) {
    if (!hasSupplierInheritance) {
      nextErrors.externalSupplierName = t('External Supplier Name is required');
    } else if (hasApId) {
      nextErrors.externalSupplierName = t(
        'External Supplier Name cannot be empty which has AP ID.'
      );
    }
  }

  return nextErrors;
};

/**
 * Mirrors Invoice Details: AP ID is always required alongside an External Supplier Name.
 * With the matching Invoice* permission a field is always required, except when the supplier
 * has an inheritance state (Inherited / NotInherited), where it is only required alongside
 * the other field.
 */
export const getSupplierRequiredFieldMarkers = ({
  values,
  requiredFields,
  hasSupplierInheritance,
}: {
  values: Pick<SupplierFormValues, 'apId' | 'externalSupplierName'>;
  requiredFields: SupplierRequiredFields;
  hasSupplierInheritance: boolean;
}): SupplierRequiredFields => ({
  apId:
    Boolean(values.externalSupplierName.trim()) || (requiredFields.apId && !hasSupplierInheritance),
  externalSupplierName:
    requiredFields.externalSupplierName && (!hasSupplierInheritance || Boolean(values.apId.trim())),
});
